/**
 * Job Postings List Component
 * Displays and manages company's job postings
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus,
  Briefcase, 
  MapPin, 
  Calendar, 
  Clock,
  Eye,
  Edit2,
  Trash2,
  Users,
  DollarSign,
  Search,
  Filter,
  ChevronDown,
  ChevronUp,
  MoreVertical,
  Copy,
  ExternalLink,
  AlertCircle,
  CheckCircle,
  XCircle,
  Pause,
  Play,
  BarChart3,
  Loader2,
  Building,X
} from 'lucide-react';
import { useCompanyAuth } from '../../../core/contexts/CompanyAuthContext';
import { 
  getCompanyJobPostings, 
  updateJobPosting, 
  deleteJobPosting,
  getJobAnalytics 
} from '../services/companyService';
import JobPostingForm from './JobPostingForm';

/**
 * Job Postings List Component
 * 
 * @returns {JSX.Element} Job postings list
 */
const JobPostingsList = () => {
  const navigate = useNavigate();
  const { companyProfile, isVerified } = useCompanyAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState({});
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    category: '',
    searchTerm: '',
    dateRange: 'all'
  });
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    paused: 0,
    expired: 0,
    draft: 0
  });

  // Job statuses
  const jobStatuses = [
    { value: 'active', label: 'Active', color: 'green', icon: CheckCircle },
    { value: 'paused', label: 'Paused', color: 'yellow', icon: Pause },
    { value: 'expired', label: 'Expired', color: 'red', icon: XCircle },
    { value: 'draft', label: 'Draft', color: 'gray', icon: Clock }
  ];

  // Job categories
  const jobCategories = [
    'Technology', 'Engineering', 'Finance', 'Marketing', 'Sales', 
    'Human Resources', 'Operations', 'Customer Service', 'Design', 
    'Data Science', 'Research', 'Healthcare', 'Education', 'Legal', 'Other'
  ];

  // Load job postings on component mount
  useEffect(() => {
    if (companyProfile) {
      loadJobPostings();
    }
  }, [companyProfile, filters]);

  // Load job postings
  const loadJobPostings = async () => {
    try {
      setLoading(true);
      
      const result = await getCompanyJobPostings(companyProfile.id, {
        status: filters.status || null,
        limitCount: 100
      });
      
      let filteredJobs = result.jobs;
      
      // Apply client-side filters
      if (filters.searchTerm) {
        const term = filters.searchTerm.toLowerCase();
        filteredJobs = filteredJobs.filter(job => 
          job.title.toLowerCase().includes(term) ||
          job.description.toLowerCase().includes(term) ||
          job.location.toLowerCase().includes(term)
        );
      }
      
      if (filters.category) {
        filteredJobs = filteredJobs.filter(job => job.category === filters.category);
      }
      
      if (filters.dateRange !== 'all') {
        const now = new Date();
        const cutoffDate = new Date();
        
        switch (filters.dateRange) {
          case 'week':
            cutoffDate.setDate(now.getDate() - 7);
            break;
          case 'month':
            cutoffDate.setMonth(now.getMonth() - 1);
            break;
          case 'quarter':
            cutoffDate.setMonth(now.getMonth() - 3);
            break;
        }
        
        filteredJobs = filteredJobs.filter(job => 
          new Date(job.createdAt) >= cutoffDate
        );
      }
      
      setJobs(filteredJobs);
      
      // Calculate stats
      const statsData = filteredJobs.reduce((acc, job) => {
        acc.total++;
        acc[job.status] = (acc[job.status] || 0) + 1;
        return acc;
      }, { total: 0, active: 0, paused: 0, expired: 0, draft: 0 });
      
      setStats(statsData);
      
    } catch (error) {
      console.error('Error loading job postings:', error);
    } finally {
      setLoading(false);
    }
  };

  // Update job status
  const handleStatusUpdate = async (jobId, newStatus) => {
    try {
      setUpdating(prev => ({ ...prev, [jobId]: true }));
      
      await updateJobPosting(jobId, { status: newStatus }, companyProfile.id);
      
      // Update local state
      setJobs(prev => prev.map(job => 
        job.id === jobId ? { ...job, status: newStatus, updatedAt: new Date() } : job
      ));
      
      // Update stats
      setStats(prev => {
        const newStats = { ...prev };
        const oldJob = jobs.find(job => job.id === jobId);
        if (oldJob) {
          newStats[oldJob.status] = Math.max(0, newStats[oldJob.status] - 1);
        }
        newStats[newStatus] = (newStats[newStatus] || 0) + 1;
        return newStats;
      });
      
    } catch (error) {
      console.error('Error updating job status:', error);
    } finally {
      setUpdating(prev => ({ ...prev, [jobId]: false }));
    }
  };

  // Delete job posting
  const handleDeleteJob = async (jobId) => {
    if (!window.confirm('Are you sure you want to delete this job posting? This action cannot be undone.')) {
      return;
    }
    
    try {
      setUpdating(prev => ({ ...prev, [jobId]: true }));
      
      await deleteJobPosting(jobId, companyProfile.id);
      
      // Remove from local state
      setJobs(prev => prev.filter(job => job.id !== jobId));
      
      // Update stats
      setStats(prev => {
        const newStats = { ...prev };
        const deletedJob = jobs.find(job => job.id === jobId);
        if (deletedJob) {
          newStats.total = Math.max(0, newStats.total - 1);
          newStats[deletedJob.status] = Math.max(0, newStats[deletedJob.status] - 1);
        }
        return newStats;
      });
      
    } catch (error) {
      console.error('Error deleting job posting:', error);
    } finally {
      setUpdating(prev => ({ ...prev, [jobId]: false }));
    }
  };

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // Get status configuration
  const getStatusConfig = (status) => {
    return jobStatuses.find(s => s.value === status) || jobStatuses[0];
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return 'N/A';
    const d = date instanceof Date ? date : new Date(date);
    return d.toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Format salary range
  const formatSalary = (salaryRange) => {
    if (!salaryRange || (!salaryRange.min && !salaryRange.max)) return 'Salary not specified';
    
    const currency = salaryRange.currency || 'ZAR';
    const symbol = currency === 'ZAR' ? 'R' : currency === 'USD' ? '$' : '€';
    
    if (salaryRange.min && salaryRange.max) {
      return `${symbol}${salaryRange.min.toLocaleString()} - ${symbol}${salaryRange.max.toLocaleString()} ${salaryRange.period || 'monthly'}`;
    } else if (salaryRange.min) {
      return `From ${symbol}${salaryRange.min.toLocaleString()} ${salaryRange.period || 'monthly'}`;
    } else if (salaryRange.max) {
      return `Up to ${symbol}${salaryRange.max.toLocaleString()} ${salaryRange.period || 'monthly'}`;
    }
    
    return 'Salary not specified';
  };

  // Job Card Component
  const JobCard = ({ job }) => {
    const [showActions, setShowActions] = useState(false);
    const statusConfig = getStatusConfig(job.status);
    const isUpdating = updating[job.id];
    const isExpired = job.applicationDeadline && new Date(job.applicationDeadline) < new Date();
    
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-md transition-shadow">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-start justify-between mb-2">
              <h3 className="text-lg font-semibold text-gray-800 hover:text-green-600 cursor-pointer">
                {job.title}
              </h3>
              <div className="relative">
                <button
                  onClick={() => setShowActions(!showActions)}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                >
                  <MoreVertical className="w-4 h-4" />
                </button>
                
                {showActions && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
                    <div className="py-2">
                      <button
                        onClick={() => {
                          setEditingJob(job);
                          setShowActions(false);
                        }}
                        className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 w-full text-left"
                      >
                        <Edit2 className="w-4 h-4" />
                        <span>Edit Job</span>
                      </button>
                      
                      {job.status === 'active' ? (
                        <button
                          onClick={() => {
                            handleStatusUpdate(job.id, 'paused');
                            setShowActions(false);
                          }}
                          disabled={isUpdating}
                          className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 w-full text-left disabled:opacity-50"
                        >
                          <Pause className="w-4 h-4" />
                          <span>Pause Job</span>
                        </button>
                      ) : job.status === 'paused' ? (
                        <button
                          onClick={() => {
                            handleStatusUpdate(job.id, 'active');
                            setShowActions(false);
                          }}
                          disabled={isUpdating}
                          className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 w-full text-left disabled:opacity-50"
                        >
                          <Play className="w-4 h-4" />
                          <span>Activate Job</span>
                        </button>
                      ) : null}
                      
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(`${window.location.origin}/career-opportunities/${job.id}`);
                          setShowActions(false);
                        }}
                        className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 w-full text-left"
                      >
                        <Copy className="w-4 h-4" />
                        <span>Copy Link</span>
                      </button>
                      
                      <button
                        onClick={() => {
                          window.open(`/career-opportunities/${job.id}`, '_blank');
                          setShowActions(false);
                        }}
                        className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 w-full text-left"
                      >
                        <ExternalLink className="w-4 h-4" />
                        <span>View Public Page</span>
                      </button>
                      
                      <div className="border-t border-gray-100 my-2" />
                      
                      <button
                        onClick={() => {
                          handleDeleteJob(job.id);
                          setShowActions(false);
                        }}
                        disabled={isUpdating}
                        className="flex items-center space-x-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 w-full text-left disabled:opacity-50"
                      >
                        <Trash2 className="w-4 h-4" />
                        <span>Delete Job</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex items-center space-x-4 text-sm text-gray-600 mb-3">
              <div className="flex items-center space-x-1">
                <MapPin className="w-4 h-4" />
                <span>{job.location}</span>
                {job.isRemote && <span className="text-green-600">(Remote)</span>}
              </div>
              <div className="flex items-center space-x-1">
                <Briefcase className="w-4 h-4" />
                <span className="capitalize">{job.jobType.replace('-', ' ')}</span>
              </div>
              <div className="flex items-center space-x-1">
                <Building className="w-4 h-4" />
                <span>{job.category}</span>
              </div>
            </div>
            
            <div className="flex items-center space-x-4 text-sm text-gray-600 mb-4">
              <div className="flex items-center space-x-1">
                <Calendar className="w-4 h-4" />
                <span>Posted {formatDate(job.createdAt)}</span>
              </div>
              <div className="flex items-center space-x-1">
                <DollarSign className="w-4 h-4" />
                <span>{formatSalary(job.salaryRange)}</span>
              </div>
            </div>
          </div>
          
          {/* Status Badge */}
          <div className={`inline-flex items-center space-x-1 px-3 py-1 rounded-full text-sm font-medium ${
            statusConfig.color === 'green' ? 'bg-green-100 text-green-800' :
            statusConfig.color === 'yellow' ? 'bg-yellow-100 text-yellow-800' :
            statusConfig.color === 'red' ? 'bg-red-100 text-red-800' :
            'bg-gray-100 text-gray-800'
          }`}>
            <statusConfig.icon className="w-4 h-4" />
            <span>{statusConfig.label}</span>
          </div>
        </div>
        
        {/* Job Description Preview */}
        <div className="mb-4">
          <p className="text-gray-600 text-sm line-clamp-2">
            {job.description}
          </p>
        </div>
        
        {/* Application Deadline Warning */}
        {isExpired && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center space-x-2 text-red-600">
              <AlertCircle className="w-4 h-4" />
              <span className="text-sm font-medium">Application deadline has passed</span>
            </div>
            <p className="text-red-600 text-sm mt-1">
              Deadline was {formatDate(job.applicationDeadline)}
            </p>
          </div>
        )}
        
        {job.applicationDeadline && !isExpired && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center space-x-2 text-blue-600">
              <Clock className="w-4 h-4" />
              <span className="text-sm">
                Application deadline: {formatDate(job.applicationDeadline)}
              </span>
            </div>
          </div>
        )}
        
        {/* Job Stats */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
          <div className="flex items-center space-x-6 text-sm text-gray-600">
            <div className="flex items-center space-x-1">
              <Eye className="w-4 h-4" />
              <span>{job.viewCount || 0} views</span>
            </div>
            <div className="flex items-center space-x-1">
              <Users className="w-4 h-4" />
              <span>{job.applicationCount || 0} applications</span>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => navigate(`/company/applications?jobId=${job.id}`)}
              className="px-3 py-1 text-sm text-green-600 hover:bg-green-50 rounded-lg transition-colors"
            >
              View Applications
            </button>
            <button
              onClick={() => setSelectedJob(job)}
              className="px-3 py-1 text-sm text-gray-600 hover:bg-gray-50 rounded-lg transition-colors flex items-center space-x-1"
            >
              <BarChart3 className="w-4 h-4" />
              <span>Analytics</span>
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading job postings...</p>
        </div>
      </div>
    );
  }

  // Show create form if requested
  if (showCreateForm) {
    return (
      <JobPostingForm
        onSaved={() => {
          setShowCreateForm(false);
          loadJobPostings();
        }}
        onCancel={() => setShowCreateForm(false)}
      />
    );
  }

  // Show edit form if editing
  if (editingJob) {
    return (
      <JobPostingForm
        existingJob={editingJob}
        onSaved={() => {
          setEditingJob(null);
          loadJobPostings();
        }}
        onCancel={() => setEditingJob(null)}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">Job Postings</h1>
            <p className="text-gray-600">
              Manage your job postings and track their performance
            </p>
          </div>
          
          <button
            onClick={() => setShowCreateForm(true)}
            disabled={!isVerified}
            className={`px-6 py-3 rounded-xl font-medium flex items-center space-x-2 transition-colors ${
              isVerified
                ? 'bg-green-500 hover:bg-green-600 text-white'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            <Plus className="w-5 h-5" />
            <span>Post New Job</span>
          </button>
        </div>

        {!isVerified && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-8">
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-yellow-500 mt-0.5" />
              <div>
                <h3 className="font-medium text-yellow-800 mb-1">Account Verification Required</h3>
                <p className="text-yellow-700 text-sm">
                  Your company account must be verified before you can post new job opportunities.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <div className="bg-white rounded-xl shadow-sm p-4">
            <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
            <div className="text-sm text-gray-600">Total Jobs</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-4">
            <div className="text-2xl font-bold text-green-600">{stats.active}</div>
            <div className="text-sm text-gray-600">Active</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-4">
            <div className="text-2xl font-bold text-yellow-600">{stats.paused}</div>
            <div className="text-sm text-gray-600">Paused</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-4">
            <div className="text-2xl font-bold text-red-600">{stats.expired}</div>
            <div className="text-sm text-gray-600">Expired</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-4">
            <div className="text-2xl font-bold text-gray-600">{stats.draft}</div>
            <div className="text-sm text-gray-600">Drafts</div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-8">
          <div className="p-4 border-b border-gray-200">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center space-x-2 text-gray-700 hover:text-gray-900"
            >
              <Filter className="w-5 h-5" />
              <span className="font-medium">Filters</span>
              {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
          </div>
          
          {showFilters && (
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {/* Search */}
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    placeholder="Search jobs..."
                    value={filters.searchTerm}
                    onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
                    className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>
                
                {/* Status Filter */}
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  <option value="">All Statuses</option>
                  {jobStatuses.map(status => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </select>
                
                {/* Category Filter */}
                <select
                  value={filters.category}
                  onChange={(e) => handleFilterChange('category', e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  <option value="">All Categories</option>
                  {jobCategories.map(category => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
                
                {/* Date Range Filter */}
                <select
                  value={filters.dateRange}
                  onChange={(e) => handleFilterChange('dateRange', e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  <option value="all">All Time</option>
                  <option value="week">Last Week</option>
                  <option value="month">Last Month</option>
                  <option value="quarter">Last 3 Months</option>
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Job Postings List */}
        {jobs.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <Briefcase className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-800 mb-2">No Job Postings Found</h3>
            <p className="text-gray-600 mb-6">
              {filters.status || filters.searchTerm || filters.category ? 
                'No job postings match your current filters.' :
                'You haven\'t posted any jobs yet. Create your first job posting to start attracting talent.'
              }
            </p>
            {isVerified && (
              <button
                onClick={() => setShowCreateForm(true)}
                className="px-6 py-3 bg-green-500 text-white rounded-xl hover:bg-green-600 transition-colors"
              >
                Post Your First Job
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            {jobs.map(job => (
              <JobCard key={job.id} job={job} />
            ))}
          </div>
        )}

        {/* Job Analytics Modal */}
        {selectedJob && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl max-w-2xl w-full max-h-screen overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-gray-800">Job Analytics</h2>
                  <button
                    onClick={() => setSelectedJob(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>
              
              <div className="p-6">
                <div className="space-y-6">
                  <div>
                    <h3 className="font-semibold text-gray-800 mb-2">{selectedJob.title}</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <div className="text-2xl font-bold text-gray-900">{selectedJob.viewCount || 0}</div>
                        <div className="text-sm text-gray-600">Total Views</div>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <div className="text-2xl font-bold text-green-600">{selectedJob.applicationCount || 0}</div>
                        <div className="text-sm text-gray-600">Applications</div>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="font-medium text-gray-800 mb-2">Job Details</h4>
                    <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                      <p><strong>Posted:</strong> {formatDate(selectedJob.createdAt)}</p>
                      <p><strong>Location:</strong> {selectedJob.location}</p>
                      <p><strong>Type:</strong> {selectedJob.jobType.replace('-', ' ')}</p>
                      <p><strong>Category:</strong> {selectedJob.category}</p>
                      {selectedJob.applicationDeadline && (
                        <p><strong>Deadline:</strong> {formatDate(selectedJob.applicationDeadline)}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default JobPostingsList;